import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/common/SEOHead';
import { 
  ShoppingBagIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  ListBulletIcon,
  XMarkIcon,
  SparklesIcon,
  ArrowsRightLeftIcon,
  CurrencyDollarIcon,
  PhotoIcon,
  HeartIcon,
  EyeIcon,
  ShoppingCartIcon,
  TagIcon,
  AdjustmentsHorizontalIcon,
  StarIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { productsAPI, categoriesAPI } from '../config/api';
import { fetchCategories } from '../store/slices/categoriesSlice';
// Removed cart usage for buy-now flow
import { getImageUrl } from '../config/api';

const Products = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { categories } = useSelector((state) => state.categories);
  const { user, lab } = useSelector((state) => state.auth);
  
  // States
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterCondition, setFilterCondition] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 100000 });
  const [currentPriceRange, setCurrentPriceRange] = useState({ min: 0, max: 100000 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [favorites, setFavorites] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [hoveredProductId, setHoveredProductId] = useState(null);

  const limit = 12;

  // Fetch products
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        // Only include search if at least 2 chars to satisfy backend validator
        ...(searchTerm && searchTerm.trim().length >= 2 ? { search: searchTerm.trim() } : {}),
        sortBy: sortBy,
        sortOrder: sortOrder,
        status: 'active',
        approvalStatus: 'approved',
      };

      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterType !== 'all') params.type = filterType;
      if (filterCondition !== 'all') params.condition = filterCondition;
      if (currentPriceRange.min > 0) params.minPrice = currentPriceRange.min;
      if (currentPriceRange.max < 100000) params.maxPrice = currentPriceRange.max;

      const response = await productsAPI.getAll(params);
      setProducts(response.data.data.products || []);
      setTotalPages(response.data.data.pagination?.totalPages || 1);
      setTotalProducts(response.data.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error(t('products.fetchError'));
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    dispatch(fetchCategories({ isActive: true, limit: 100 }));
    fetchProducts();
  }, [dispatch]);

  // Refetch on filter/sort changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchProducts();
    }, searchTerm ? 500 : 0);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, filterCategory, filterType, filterCondition, sortBy, sortOrder, page, currentPriceRange]);

  // Toggle favorite
  const toggleFavorite = useCallback((productId) => {
    if (!user) {
      toast.error(t('products.loginRequired'));
      navigate('/login');
      return;
    }

    setFavorites(prev => {
      if (prev.includes(productId)) {
        toast.success(t('products.removedFromFavorites'));
        return prev.filter(id => id !== productId);
      } else {
        toast.success(t('products.addedToFavorites'));
        return [...prev, productId];
      }
    });
  }, [user, navigate]);

  // Buy now - direct checkout for single product with ownership check
  const handleBuyNow = useCallback((product, e) => {
    e?.stopPropagation();
    if (!user) {
      toast.error(t('products.loginRequired'));
      navigate('/login');
      return;
    }

    if (product.type !== 'sale') {
      toast.error(t('products.exchangeOnly'));
      return;
    }

    // Prevent buying own product
    if (lab && product.lab && (product.lab._id === lab._id || product.lab === lab._id)) {
      toast.error(t('products.cannotPurchaseOwnProducts'));
      return;
    }

    // Navigate to checkout with single item
    navigate('/checkout', {
      state: {
        product,
        quantity: 1,
      }
    });
  }, [user, navigate, lab, t]);



  // Quick view
  const openQuickView = useCallback((product) => {
    setSelectedProduct(product);
    setIsQuickViewOpen(true);
  }, []);

  // Get category name based on current language
  const getCategoryName = (category) => {
    if (!category) return t('products.uncategorized');
    const currentLang = i18n.language;
    
    if (typeof category === 'string') {
      const cat = categories.find(c => c._id === category);
      return cat?.name?.[currentLang] || cat?.name?.ar || cat?.name || t('products.uncategorized');
    }
    return category.name?.[currentLang] || category.name?.ar || category.name || t('products.uncategorized');
  };

  // Get condition label
  const getConditionLabel = (condition) => {
    return t(`products.condition.${condition}`) || condition;
  };

  // Get unit label with translation
  const getUnitLabel = (unit) => {
    if (!unit) return t('products.piece');
    
    // Common unit translations
    const unitTranslations = {
      'piece': t('products.units.piece'),
      'قطعة': t('products.units.piece'),
      'box': t('products.units.box'),
      'علبة': t('products.units.box'),
      'set': t('products.units.set'),
      'طقم': t('products.units.set'),
      'pack': t('products.units.pack'),
      'حزمة': t('products.units.pack')
    };
    
    return unitTranslations[unit] || unit;
  };

  // Render product card
  const renderProductCard = (product) => (
    <motion.div
      key={product._id}
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -5 }}
      className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden relative flex flex-col h-[520px]"
      onMouseEnter={() => setHoveredProductId(product._id)}
      onMouseLeave={() => setHoveredProductId(null)}
    >
      {/* Sale/Exchange Badge */}
      <div className="absolute top-4 left-4 z-10">
        {product.type === 'sale' ? (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white shadow-lg">
            <CurrencyDollarIcon className="w-3 h-3 ml-1" />
            {t('products.forSale')}
          </span>
        ) : (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-lg">
            <ArrowsRightLeftIcon className="w-3 h-3 ml-1" />
            {t('products.forExchange')}
          </span>
        )}
      </div>

      {/* Favorite Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleFavorite(product._id);
        }}
        className="absolute top-4 right-4 z-10 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-all duration-200 group/fav"
      >
        {favorites.includes(product._id) ? (
          <HeartIconSolid className="w-5 h-5 text-red-500 group-hover/fav:scale-110 transition-transform" />
        ) : (
          <HeartIcon className="w-5 h-5 text-gray-600 group-hover/fav:text-red-500 group-hover/fav:scale-110 transition-all" />
        )}
      </button>

      {/* Product Image */}
      <div className="relative h-64 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-50">
        {product.images && product.images.length > 0 ? (
          <motion.img
            src={getImageUrl(product.images[0].url)}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            whileHover={{ scale: 1.1 }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <PhotoIcon className="w-20 h-20 text-gray-300" />
          </div>
        )}

        {/* Stock Alert for Out of Stock */}
        {product.quantity === 0 && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="bg-red-500 text-white px-3 py-2 rounded-lg font-bold text-sm">
              {t('products.outOfStock')}
            </div>
          </div>
        )}

        {/* Quick View Overlay */}
        {product.quantity > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: hoveredProductId === product._id ? 1 : 0 }}
            className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex items-end justify-center pb-4 pointer-events-none"
          >
            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: hoveredProductId === product._id ? 0 : 20, opacity: hoveredProductId === product._id ? 1 : 0 }}
              transition={{ delay: 0.1 }}
              onClick={(e) => {
                e.stopPropagation();
                openQuickView(product);
              }}
              className="pointer-events-auto px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full text-sm font-medium text-gray-800 hover:bg-white transition-colors flex items-center gap-2 shadow-lg"
            >
              <EyeIcon className="w-4 h-4" />
              {t('products.quickView')}
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-5 flex flex-col flex-grow">
        {/* Category */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium text-gray-500">{getCategoryName(product.category)}</span>
          {product.condition && (
            <>
              <span className="text-gray-300">•</span>
              <span className="text-xs font-medium text-gray-500">{getConditionLabel(product.condition)}</span>
            </>
          )}
        </div>

        {/* Product Name */}
        <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors h-12 overflow-hidden">
          {product.name}
        </h3>

        {/* Product Description */}
        <p className="text-sm text-gray-600 mb-4 line-clamp-3 overflow-hidden flex-grow">
          {product.description}
        </p>

        {/* Rating */}
        {product.rating?.average > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <StarIcon
                  key={i}
                  className={`w-4 h-4 ${
                    i < Math.floor(product.rating.average)
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-gray-500">({product.rating.count})</span>
          </div>
        )}

        {/* Price/Exchange Info */}
        <div className="flex items-center justify-between mb-4">
          {product.quantity === 0 ? (
            <div className="flex items-center gap-2 text-red-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">{t('products.outOfStock')}</span>
            </div>
          ) : product.type === 'sale' ? (
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-blue-600">
                {product.price?.toLocaleString('ar-SA')}
              </span>
              <span className="text-sm text-gray-500">{product.currency || t('products.sar')}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <ArrowsRightLeftIcon className="w-5 h-5 text-teal-600" />
              <span className="text-lg font-semibold text-teal-600">{t('products.availableForExchange')}</span>
            </div>
          )}
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <TagIcon className="w-4 h-4" />
            <span>{product.quantity} {getUnitLabel(product.unit)}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 mt-auto">
          {product.quantity === 0 ? (
            <div className="flex-1 bg-red-50 border border-red-200 rounded-xl p-3 text-center">
              <div className="flex items-center justify-center gap-2 text-red-600">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">{t('products.notAvailable')}</span>
              </div>
            </div>
          ) : product.type === 'sale' ? (
            <>
              <button
                onClick={(e) => handleBuyNow(product, e)}
                className="flex-1 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white px-4 py-2.5 rounded-xl font-medium hover:opacity-95 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <ShoppingCartIcon className="w-5 h-5" />
                {t('products.buyNow')}
              </button>
              <button
                onClick={() => navigate(`/products/${product.slug || product._id}`)}
                className="px-3 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                <EyeIcon className="w-5 h-5 text-gray-700" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate(`/products/${product.slug || product._id}`)}
                className="flex-1 bg-gradient-to-r from-teal-600 to-teal-700 text-white px-4 py-2.5 rounded-xl font-medium hover:from-teal-700 hover:to-teal-800 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <ArrowsRightLeftIcon className="w-5 h-5" />
                {t('products.requestExchange')}
              </button>
              <button
                onClick={() => navigate(`/products/${product.slug || product._id}`)}
                className="px-3 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                <EyeIcon className="w-5 h-5 text-gray-700" />
              </button>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );

  // Generate SEO data
  const generateSEOData = () => {
    const categoryName = filterCategory !== 'all' 
      ? categories.find(cat => cat._id === filterCategory)?.name?.[i18n.language] || 
        categories.find(cat => cat._id === filterCategory)?.name?.ar || 
        t('seo.defaultCategory')
      : '';
    
    const title = searchTerm 
      ? t('seo.searchTitle', { query: searchTerm })
      : categoryName 
        ? t('seo.categoryTitle', { category: categoryName })
        : t('seo.defaultTitle');
    
    const description = searchTerm
      ? t('seo.searchDescription', { query: searchTerm })
      : categoryName
        ? t('seo.categoryDescription', { category: categoryName })
        : t('seo.defaultDescription');
    
    const keywords = [
      t('seo.keywords.labEquipment'),
      t('seo.keywords.medicalDevices'),
      t('seo.keywords.bloodAnalysis'),
      t('seo.keywords.diagnosticEquipment'),
      t('seo.keywords.saudiLabs'),
      t('seo.keywords.medicalTools'),
      t('seo.keywords.forSale'),
      t('seo.keywords.forExchange'),
      categoryName,
      searchTerm
    ].filter(Boolean);
    
    // Generate canonical URL
    const canonicalUrl = searchTerm 
      ? `/search?q=${encodeURIComponent(searchTerm)}`
      : filterCategory !== 'all'
        ? `/products?category=${filterCategory}`
        : '/products';
    
    return { 
      title, 
      description, 
      keywords,
      canonical: canonicalUrl,
      url: canonicalUrl
    };
  };

  const seoData = generateSEOData();

  // إنشاء Product List Schema
  const productListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "منتجات معدات المختبرات الطبية",
    "description": "تصفح أكبر تشكيلة من معدات المختبرات الطبية وأجهزة التحليل في السعودية",
    "url": "https://badeil.com/products",
    "numberOfItems": products.length,
    "itemListElement": products.slice(0, 20).map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.name,
        "description": product.description,
        "image": product.images?.[0]?.url,
        "url": `https://badeil.com/products/${product.slug || product._id}`,
        "brand": {
          "@type": "Brand",
          "name": product.brand || "غير محدد"
        },
        "category": product.category?.name?.ar || product.category,
        "offers": product.type === 'sale' ? {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": "SAR",
          "availability": product.quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "seller": {
            "@type": "Organization",
            "name": "بديل - منصة المختبرات الطبية"
          }
        } : {
          "@type": "Offer",
          "price": product.actualValue?.amount || 0,
          "priceCurrency": "SAR",
          "availability": product.quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "seller": {
            "@type": "Organization",
            "name": "بديل - منصة المختبرات الطبية"
          }
        }
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-teal-50/20">
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        type="website"
        structuredData={productListSchema}
      />
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-teal-600"></div>
        <div className="absolute inset-0 opacity-10 bg-[linear-gradient(0deg,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[length:40px_40px]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
              <SparklesIcon className="w-4 h-4" />
              {t('products.trustedPlatform')}
            </div>
            <h1 className="text-5xl font-bold mb-6">
              {t('products.title')}
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
              {t('products.subtitle')}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="text-center"
              >
                <div className="text-3xl font-bold text-white mb-1">{totalProducts}+</div>
                <div className="text-blue-100 text-sm">{t('products.availableProducts')}</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                className="text-center"
              >
                <div className="text-3xl font-bold text-white mb-1">{categories.length}+</div>
                <div className="text-blue-100 text-sm">{t('products.diverseCategories')}</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="text-center"
              >
                <div className="text-3xl font-bold text-white mb-1">24/7</div>
                <div className="text-blue-100 text-sm">{t('products.continuousSupport')}</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filters & Products Section */}
      <section className="container mx-auto px-4 py-12">
        {/* Search & Filters Bar */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Search */}
            <div className="lg:col-span-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('products.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pr-12 pl-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="lg:col-span-2">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white cursor-pointer"
              >
                <option value="all">{t('products.allCategories')}</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name?.[i18n.language] || cat.name?.ar || cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Type Filter */}
            <div className="lg:col-span-2">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white cursor-pointer"
              >
                <option value="all">{t('products.allTypes')}</option>
                <option value="sale">{t('products.forSale')}</option>
                <option value="exchange">{t('products.forExchange')}</option>
              </select>
            </div>

            {/* Sort */}
            <div className="lg:col-span-2">
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [sort, order] = e.target.value.split('-');
                  setSortBy(sort);
                  setSortOrder(order);
                }}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white cursor-pointer"
              >
                <option value="createdAt-desc">{t('products.newest')}</option>
                <option value="createdAt-asc">{t('products.oldest')}</option>
                <option value="price-asc">{t('products.priceLowHigh')}</option>
                <option value="price-desc">{t('products.priceHighLow')}</option>
                <option value="name-asc">{t('products.alphabetical')}</option>
                <option value="name-desc">{t('products.alphabetical')}</option>
              </select>
            </div>

            {/* View Mode & Advanced Filters */}
            <div className="lg:col-span-2 flex gap-2">
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className={`flex-1 p-3 rounded-xl border transition-all ${
                  viewMode === 'grid' 
                    ? 'bg-blue-50 border-blue-500 text-blue-600' 
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {viewMode === 'grid' ? (
                  <Squares2X2Icon className="w-5 h-5 mx-auto" />
                ) : (
                  <ListBulletIcon className="w-5 h-5 mx-auto" />
                )}
              </button>
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex-1 p-3 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
              >
                <AdjustmentsHorizontalIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-gray-600">
            {t('products.showingResults', { 
              from: products.length, 
              total: totalProducts 
            })}
          </div>
        </div>

        {/* Products Grid/List */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full"
            />
          </div>
        ) : products.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-16 text-center"
          >
            <ShoppingBagIcon className="w-24 h-24 text-gray-300 mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('products.noProductsFound')}</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {t('products.noProductsMessage')}
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterCategory('all');
                setFilterType('all');
                setFilterCondition('all');
                setCurrentPriceRange({ min: 0, max: 100000 });
              }}
              className="px-6 py-3 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-xl font-medium hover:opacity-95 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {t('common.reset')}
            </button>
          </motion.div>
        ) : (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
            : 'space-y-4'
          }>
            <AnimatePresence mode="popLayout">
              {products.map((product) => renderProductCard(product))}
            </AnimatePresence>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-12">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('common.previous')}
            </button>
            
            <div className="flex items-center gap-1">
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-10 h-10 rounded-lg font-medium transition-all ${
                      page === pageNum
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default Products;
